import React, { useCallback, useEffect, useTransition } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { Outlet, Navigate, useLocation, useNavigate, useMatches } from 'react-router';
import { useAuth } from '../../hooks/useAuth';
import { Layout } from './Layout';
import { AccessDenied } from './AccessDenied';
import LoginPage from '../LoginPage';
import { menuItems } from '../../data/menuItems';
import { ROLE_EXTERNAL, ROLE_PARTNER, BOUNDARY_ROLES } from '../../constants/roles';
import { pathToMenuId, menuIdToPath } from '../../utils/navigation';

export const AppLayout: React.FC = () => {
  const { isAuthenticated, isInitialized, user, logout, canAccessMenu } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const matches = useMatches();
  const [isNavigating, startTransition] = useTransition();

  // Get route metadata from handle
  const currentHandle = matches[matches.length - 1]?.handle as
    | { menuId?: string; fullWidth?: boolean }
    | undefined;

  const selectedMenu = currentHandle?.menuId || pathToMenuId(location.pathname);
  const fullWidth = currentHandle?.fullWidth ?? false;

  // Check if user has permission for the current route's menu item
  const isRouteAllowed = !selectedMenu || canAccessMenu(selectedMenu);

  // Update document title
  useEffect(() => {
    const menuItem =
      menuItems.find((item) => item.id === selectedMenu) ||
      menuItems
        .flatMap((item) => item.children || [])
        .find((child) => child.id === selectedMenu);
    document.title = menuItem ? `${menuItem.label} - DRIS` : 'DRIS';
  }, [selectedMenu]);

  const handleMenuSelect = useCallback((menuId: string) => {
    // Prefer the menu item's own declared `path` — the menu-id→path map is a router-migration
    // bridge that silently falls back to /dashboard for ids it never learned (which left newer
    // items, e.g. the partner-* group, rendering in the sidebar but inert on click).
    const item =
      menuItems.find((i) => i.id === menuId) ||
      menuItems.flatMap((i) => i.children || []).find((c) => c.id === menuId);
    const path = item?.path || menuIdToPath(menuId);
    startTransition(() => {
      navigate(path);
    });
  }, [navigate, startTransition]);

  const handleLogout = useCallback(async () => {
    // Await logout BEFORE navigating: logout() clears the token and dispatches LOGOUT only after
    // the server round-trip resolves. Navigating to a protected route synchronously would remount
    // the dashboard mid-teardown and fire authenticated requests just as the token is wiped (401s).
    // Once isAuthenticated flips to false, AppLayout renders <LoginPage/> and never mounts the
    // Outlet — so navigating to /dashboard here only resets the landing route for the next login.
    await logout();
    navigate('/dashboard');
  }, [logout, navigate]);

  // Show a branded loading splash ONLY during the very first auth check (silent cookie refresh on
  // load). Previously this returned null → a blank white page for as long as the initial refresh
  // took; the initial refresh is now capped to a single attempt (AuthService.INITIAL_REFRESH_MAX_ATTEMPTS)
  // so this splash can never hang for the full retry budget.
  // Do NOT gate on isLoading: a login attempt toggles isLoading true, and blanking here would
  // unmount LoginPage mid-login — so LoginPage's setShow2FADialog(true) would run on a dead
  // component and the 2FA dialog would never appear (the form would just reset). Once the app is
  // initialized, LoginPage stays mounted through the login round-trip and owns its own spinner.
  if (!isInitialized) {
    return (
      <Box
        role="status"
        aria-live="polite"
        aria-busy="true"
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 3,
          bgcolor: 'background.default',
          px: 2,
        }}
      >
        <Typography
          variant="h3"
          sx={{ fontWeight: 700, letterSpacing: '0.15em', color: 'primary.main' }}
        >
          DRIS
        </Typography>
        <CircularProgress color="primary" aria-label="Loading" />
        <Typography variant="body2" color="text.secondary">
          Signing you in…
        </Typography>
      </Box>
    );
  }

  if (!isAuthenticated) {
    return <LoginPage />;
  }

  // Portal confinement & redirect precedence.
  //
  // External and Partner are portal-only personas with no access to the internal app surface.
  // The role model guarantees a Partner account carries ONLY the Partner role (and likewise an
  // External-only account), so the personas are normally mutually exclusive. We still resolve a
  // single, explicit precedence here so a (latent) dual-role user is never silently trapped
  // between two competing redirects:
  //
  //   1. If the user has ANY internal (staff) role, they stay on the internal surface — no
  //      portal confinement at all.
  //   2. Otherwise, if they are a Partner, confine them to /partner (Partner wins over External).
  //   3. Otherwise, if they are External, confine them to /portal.
  const roles = user?.roles ?? [];
  const isPartner = roles.includes(ROLE_PARTNER);
  const isExternal = roles.includes(ROLE_EXTERNAL);
  // A role makes a user "internal" only if it is NOT a boundary (portal-confined) role.
  // BOUNDARY_ROLES includes PartnerProductManager, so a (Partner + PartnerProductManager) account
  // is treated as a boundary user and confined to the partner portal — not left on the internal
  // surface (where it would land on /dashboard → AccessDenied).
  const isInternal = roles.some((r) => !BOUNDARY_ROLES.includes(r));

  if (!isInternal && (isPartner || isExternal)) {
    // Pick the single portal this persona is confined to (Partner takes precedence).
    const portalBase = isPartner ? '/partner' : '/portal';
    const onPortal =
      location.pathname === portalBase || location.pathname.startsWith(`${portalBase}/`);
    if (!onPortal) {
      // Plan P12: an External account's /profile is the portal Profile page under the new IA
      // (billingEnabled). Dark, it keeps bouncing to the portal landing exactly as before.
      if (!isPartner && location.pathname === '/profile' && user?.billingEnabled === true) {
        return <Navigate to="/portal/profile" replace />;
      }
      return <Navigate to={portalBase} replace />;
    }
  }

  return (
    <Layout
      menuItems={menuItems}
      selectedMenu={selectedMenu}
      onMenuSelect={handleMenuSelect}
      onLogout={handleLogout}
      userEmail={user?.email}
      user={user || undefined}
      fullWidth={fullWidth}
      isNavigating={isNavigating}
    >
      {isRouteAllowed ? <Outlet /> : <AccessDenied />}
    </Layout>
  );
};
